interface SentimentInfo {
  type?: string;
}

interface StatusInfo {
  label?: string;
  sentiment?: SentimentInfo;
}

/**
 * Formats an ISO date string to CDT (Central Daylight Time) format
 *
 * @param isoString - ISO date string to format
 * @returns Formatted date string in CDT timezone or empty string if input is invalid
 */
export const formatToCDT = (isoString?: string): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Chicago',
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const get = (t: string): string =>
    parts.find((p) => p.type === t)?.value || '';
  return `${get('month')}/${get('day')}/${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`;
};

/**
 * Capitalizes the first letter of a string
 *
 * @param s - String to capitalize
 * @returns String with first letter capitalized or empty string if input is falsy
 */
export const capitalize = (s: string | undefined = ''): string =>
  s ? `${s[0].toUpperCase()}${s.slice(1)}` : '';

/**
 * Determines if the next step should be shown based on status information
 *
 * @param statusInfo - Status information object
 * @returns True if next step should be shown (Failed status or Completed with negative sentiment)
 */
export const getShouldShowNextStep = (statusInfo?: StatusInfo): boolean => {
  if (!statusInfo) return false;
  return (
    statusInfo.label === 'Failed' ||
    (statusInfo.label === 'Completed' &&
      statusInfo.sentiment?.type === 'negative')
  );
};
